import { Component, NgZone, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import {
  BrowserAuthError,
  BrowserAuthErrorCodes,
} from '@azure/msal-browser';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '../../services/auth.service';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit {
  private authService = inject(AuthService);
  private router = inject(Router);
  private zone = inject(NgZone);

  isLoading = false;
  errorMessage = '';

  /**
   * True when a previous sign-in attempt was abandoned (cancelled by the user
   * or timed out) and MSAL may still hold that interaction open. The next
   * sign-in click passes it as overrideInteractionInProgress.
   */
  private abandonedInteraction = false;

  ngOnInit(): void {
    // First check if there's a stored redirect URL and user is already authenticated
    const redirectUrl = sessionStorage.getItem('redirectUrl');
    if (redirectUrl) {
      this.authService.isLoggedInAsync().then((isLoggedIn) => {
        if (isLoggedIn) {
          sessionStorage.removeItem('redirectUrl');
          this.router.navigateByUrl(redirectUrl);
        }
      });
    }

    try {
      // bootstrap$ handles the MSAL redirect promise and backend login in one shared call.
      // shareReplay ensures no duplicate POST /api/auth/login even if multiple components subscribe.
      this.authService.bootstrap$.subscribe({
        next: (loginResponse) => {
          if (loginResponse) {
            const finalRedirectUrl = sessionStorage.getItem('redirectUrl');
            sessionStorage.removeItem('redirectUrl');
            this.router.navigateByUrl(finalRedirectUrl || '/dashboard');
          }
          // null means not authenticated — stay on the login page
        },
        error: (error) => {
          console.error('Auth bootstrap error:', error);
          this.errorMessage =
            'Login successful but failed to load user profile. Please try again or contact support.';
        },
      });
    } catch (error) {
      console.error('Error in ngOnInit:', error);
      this.errorMessage =
        'An error occurred. Please try again or contact support.';
    }
  }

  async loginWithMicrosoft(): Promise<void> {
    try {
      this.isLoading = true;
      this.errorMessage = '';

      // Clicking sign in is the deliberate user action that licenses clearing
      // a previously abandoned interaction (see AuthService.loginPopup).
      const overrideInteractionInProgress = this.abandonedInteraction;
      this.abandonedInteraction = false;

      await firstValueFrom(
        this.authService.loginPopup({ overrideInteractionInProgress }),
      );

      // MSAL v5 resolves popup logins from a BroadcastChannel message, which
      // zone.js does not patch — from here on we are outside the Angular
      // zone, so a navigation started here would not render. Re-enter first.
      this.zone.run(() => {
        const redirectUrl = sessionStorage.getItem('redirectUrl');
        sessionStorage.removeItem('redirectUrl');
        this.router.navigateByUrl(redirectUrl || '/dashboard');
      });
    } catch (error) {
      // Rejections arrive outside the Angular zone too (see above).
      this.zone.run(() => {
        // handleLoginError guards its own async work, so this catch is a
        // backstop — but it still resets the button, because a swallowed
        // rejection here would leave it disabled with nothing explaining why.
        this.handleLoginError(error).catch((handlerError) => {
          console.error('Login error handling failed:', handlerError);
          this.errorMessage =
            'Failed to log you in and authenticate. Please try again or contact support.';
          this.isLoading = false;
        });
      });
    }
  }

  /**
   * Abandons the in-flight sign-in so the button becomes usable again.
   *
   * Needed because COOP leaves MSAL unable to see the popup at all: if the
   * user closes it, nothing rejects until the bridge wait times out
   * (system.popupBridgeTimeout). Without this the button would sit disabled
   * on "Signing in..." for that whole period with no way to retry.
   */
  cancelSignIn(): void {
    this.isLoading = false;
    this.errorMessage = '';
    this.abandonedInteraction = true;
  }

  private async handleLoginError(error: unknown): Promise<void> {
    if (error instanceof BrowserAuthError) {
      switch (error.errorCode) {
        // The bridge wait expired. Under COOP this is what a closed or
        // abandoned popup looks like — MSAL cannot observe the popup, so it
        // cannot tell "user gave up" from "took too long". Treat it as a
        // retryable non-event rather than a support-worthy failure.
        case BrowserAuthErrorCodes.timedOut:
          this.abandonedInteraction = true;
          this.errorMessage =
            'Sign-in was not completed. Please try signing in again.';
          this.isLoading = false;
          return;

        // Only reachable on non-bridge popup paths (the popup relay polls
        // window.closed); kept so those flows reset quietly.
        case BrowserAuthErrorCodes.userCancelled:
          this.abandonedInteraction = true;
          this.isLoading = false;
          return;

        // Our own override cleared the stale interaction — expected, silent.
        case BrowserAuthErrorCodes.interactionInProgressCancelled:
          this.isLoading = false;
          return;

        // A previous attempt is still registered and this one did not override
        // it. Reset so the next click can (that click is the explicit user
        // action MSAL requires before cancelling a pending interaction).
        case BrowserAuthErrorCodes.interactionInProgress:
          this.abandonedInteraction = true;
          this.errorMessage =
            'A sign-in attempt is already in progress. Please try again.';
          this.isLoading = false;
          return;

        // Popup could not open (blocked) — fall back to the full-page
        // redirect flow, which bootstrap$ completes on the way back in.
        case BrowserAuthErrorCodes.popupWindowError:
        case BrowserAuthErrorCodes.emptyWindowError:
          try {
            await this.authService.loginRedirect();
            // Navigation is underway — keep the button disabled until the
            // page unloads.
            return;
          } catch (redirectError) {
            // The fallback itself failed, typically interaction_in_progress
            // left behind by the blocked popup. Fall through to the generic
            // handling below: the user gets a message and a usable button
            // instead of a spinner that never resolves.
            console.error('Redirect fallback failed:', redirectError);
            this.abandonedInteraction = true;
            break;
          }
      }
    }

    console.error('Login error:', error);
    this.errorMessage =
      'Failed to log you in and authenticate. Please try again or contact support.';
    this.isLoading = false;
  }
}
